import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Star, EyeOff, Eye, Trash2 } from "lucide-react";
import { AdminTabs } from "@/components/AdminTabs";
import { FilterPills } from "@/components/FilterPills";

const fmt = (s) => (s ? new Date(s).toLocaleDateString() : "—");

const Stars = ({ n }) => (
  <span className="inline-flex items-center gap-0.5" title={`${n} of 5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} className={`w-3.5 h-3.5 ${i <= n ? "fill-[#CC5A3A] text-[#CC5A3A]" : "text-[#D8D6CE]"}`} />
    ))}
  </span>
);

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [status, setStatus] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = status !== "all" ? { status } : {};
      const { data } = await api.get("/admin/reviews", { params });
      setReviews(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const toggleHide = async (r) => {
    try {
      await api.patch(`/admin/reviews/${r.id}`, { hidden: !r.hidden });
      toast.success(r.hidden ? "Review restored" : "Review hidden");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const remove = async (r) => {
    if (!window.confirm("Permanently delete this review? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/reviews/${r.id}`);
      toast.success("Review deleted");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="container-x py-12" data-testid="admin-reviews-page">
      <AdminTabs />
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold mb-1">Review moderation</h1>
      <p className="text-sm text-[#5C6B64] mb-6">Hide inappropriate reviews (excluded from ratings) or delete them entirely.</p>

      <FilterPills
        options={[
          { key: "all", label: "All" },
          { key: "visible", label: "Visible" },
          { key: "hidden", label: "Hidden" },
        ]}
        value={status}
        onChange={setStatus}
        testidPrefix="admin-reviews-filter"
      />

      <div className="mt-6 overflow-x-auto card-soft p-0">
        <table className="w-full text-sm" data-testid="admin-reviews-table">
          <thead>
            <tr className="text-left text-[#8A9A86] border-b border-[#1E3F33]/10">
              <th className="p-3 font-medium">Collector</th>
              <th className="p-3 font-medium">Rating</th>
              <th className="p-3 font-medium">Comment</th>
              <th className="p-3 font-medium">By</th>
              <th className="p-3 font-medium">Date</th>
              <th className="p-3 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="p-6 text-center text-[#8A9A86]">Loading…</td></tr>
            ) : reviews.length === 0 ? (
              <tr><td colSpan={6} className="p-6 text-center text-[#8A9A86]" data-testid="admin-reviews-empty">No reviews found.</td></tr>
            ) : (
              reviews.map((r) => (
                <tr key={r.id} className={`border-b border-[#1E3F33]/6 align-top ${r.hidden ? "bg-[#CC5A3A]/5" : ""}`} data-testid={`admin-review-row-${r.id}`}>
                  <td className="p-3">
                    <Link to={`/collectors/${r.collector_id}`} className="text-[#1E3F33] font-medium hover:text-[#CC5A3A]">{r.collector_name}</Link>
                    {r.hidden && <span className="ml-2 text-[10px] uppercase tracking-wide text-[#CC5A3A] font-semibold">Hidden</span>}
                  </td>
                  <td className="p-3"><Stars n={r.rating} /></td>
                  <td className="p-3 max-w-md text-[#5C6B64]">{r.comment || <span className="italic text-[#8A9A86]">No comment</span>}</td>
                  <td className="p-3 text-[#5C6B64]">{r.customer_name}</td>
                  <td className="p-3 text-[#8A9A86] whitespace-nowrap">{fmt(r.created_at)}</td>
                  <td className="p-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => toggleHide(r)} data-testid={`admin-review-hide-${r.id}`} className="btn-secondary text-xs py-1.5 inline-flex items-center gap-1">
                        {r.hidden ? <><Eye className="w-3.5 h-3.5" /> Unhide</> : <><EyeOff className="w-3.5 h-3.5" /> Hide</>}
                      </button>
                      <button onClick={() => remove(r)} data-testid={`admin-review-delete-${r.id}`} className="text-[#CC5A3A] hover:bg-[#CC5A3A]/10 rounded-lg p-1.5" aria-label="Delete">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
